import { NoRecords } from '@/components/noDataMessages';
import { PaginationFooter } from '@/components/pagination';
import { CardListSkeleton } from '@/components/skeletonLoaders';
import { CardHeader, CardTitle } from '@/components/ui/largeCard';
import { DatasetDownloadsQuery, DatasetDownloadsQueryVariables } from '@/gql/graphql';
import { useIntParam } from '@/hooks/useParam';
import useQuery from '@/hooks/useQuery';
import { DownloadResult } from '@/routes/user/downloads/downloadResult';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDatasetKeyLoaderData } from '..';
import { DownloadAsTSVLink } from '@/components/cardHeaderActions/downloadAsTSVLink';

export function Downloads() {
  const { dataset } = useDatasetKeyLoaderData().data;
  const [offset, setOffset] = useIntParam({
    key: 'offset',
    defaultValue: 0,
    hideDefault: true,
    preventScrollReset: false,
  });

  const { data, load, loading } = useQuery<DatasetDownloadsQuery, DatasetDownloadsQueryVariables>(
    DOWNLOADS_QUERY,
    {
      throwAllErrors: true,
      lazyLoad: true,
    }
  );

  useEffect(() => {
    load({
      variables: {
        datasetKey: dataset.key,
        limit: 20,
        offset,
      },
    });
  }, [offset, load, dataset.key]);

  if (loading || !data) return <CardListSkeleton />;

  const downloads = data?.datasetDownloads;

  return (
    <section>
      {downloads?.count === 0 && (
        <>
          <NoRecords messageId="dataset.noDownloads" />
        </>
      )}
      {downloads && downloads.count > 0 && (
        <>
          <CardHeader
            id="downloads"
            className="g-flex-col md:g-flex-row g-items-start md:g-items-center g-justify-between"
          >
            <CardTitle>
              <FormattedMessage id="counts.nDownloads" values={{ total: downloads.count }} />
            </CardTitle>
            <DownloadAsTSVLink
              tsvUrl={`${
                import.meta.env.PUBLIC_API_V1
              }/occurrence/download/statistics/export?datasetKey=${dataset.key}`}
            />
          </CardHeader>

          {downloads.results.map((download) => (
            <div key={download?.download?.key}>
              <div className="g-text-end g-mb-2 g-text-sm g-text-slate-600">
                <FormattedMessage
                  id="dataset.nFromDataset"
                  values={{ total: download?.numberRecords || 0 }}
                />
              </div>
              {download?.download && <DownloadResult download={download.download} hideStatus />}
            </div>
          ))}

          {downloads.count > downloads.limit && (
            <PaginationFooter
              offset={downloads.offset}
              count={downloads.count}
              limit={downloads.limit}
              onChange={(x) => setOffset(x)}
            />
          )}
        </>
      )}
    </section>
  );
}

const DOWNLOADS_QUERY = /* GraphQL */ `
  query DatasetDownloads($datasetKey: ID!, $limit: Int, $offset: Int) {
    datasetDownloads(datasetKey: $datasetKey, limit: $limit, offset: $offset) {
      limit
      offset
      count
      endOfRecords
      results {
        numberRecords
        download {
          ...DownloadResult
        }
      }
    }
  }
`;
